import React, { useContext } from 'react';
import { connect as formikConnect, getIn } from 'formik';
import { FormStateContext } from 'web-shared/modules/FormStateProvider';
import { useUnknownFormActions, useUnknownFormField } from 'web-shared/modules/FormStateProvider/const';
import TextField, { Props } from '.';

export const withFormikTextFieldWrapper = (WrappedComponent: React.ComponentType<Props>) => formikConnect<Props>(({ formik, ...props }) => {

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;
    if (props.onChange) {
      props.onChange(event);
    } else {
      formik.setFieldValue(props.name, value);
    }
  };

  const handleBlur = (event: any) => {
    if (props.onBlur) {
      props.onBlur(event);
    } else {
      formik.setFieldTouched(props.name);
    }
  };

  const handleClear = (defaultValue: Props['value']) => {
    if (props.onClear) props.onClear(defaultValue);
    else formik.setFieldValue(props.name, defaultValue === null ? null : defaultValue || '');
    formik.setFieldTouched(props.name, true);
  };

  const error: any = getIn(formik.errors, props.name);
  const touched: boolean = getIn(formik.touched, props.name);
  const value: string | null | undefined = getIn(formik.values, props.name);
  const showErr = !!(error && touched);

  return (
    <WrappedComponent
      {...props}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onClear={handleClear}
      error={showErr}
      helperText={showErr ? error : props.helperText}
    />
  );
});

const OptimizedFormikTextField = (props: Props) => {
  const { setFieldValue, setFieldTouched } = useUnknownFormActions();
  const value: any = useUnknownFormField(state => getIn(state.values, props.name));
  const touched: boolean = useUnknownFormField(state => getIn(state.touched, props.name));
  const error: any = useUnknownFormField(state => getIn(state.errors, props.name));
  const showErr = !!(error && touched);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;
    if (props.onChange) {
      props.onChange(event);
    } else {
      setFieldValue(props.name, value);
    }
  };

  const handleBlur = (event: any) => {
    if (props.onBlur) {
      props.onBlur(event);
    } else {
      setFieldTouched(props.name, true);
    }
  };

  const handleClear = (defaultValue: Props['value']) => {
    if (props.onClear) props.onClear(defaultValue);
    else setFieldValue(props.name, defaultValue === null ? null : defaultValue || '');
    setFieldTouched(props.name, true);
  };

  return (
    <TextField
      {...props}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onClear={handleClear}
      error={showErr}
      helperText={showErr ? error : props.helperText}
    />
  );
};

const ConnectedFormikTextField = withFormikTextFieldWrapper(TextField);

export default React.memo((props: Props) => {
  const hasFormikProvider = useContext(FormStateContext);
  if (!hasFormikProvider) {
    return <ConnectedFormikTextField {...props} />;
  }
  return <OptimizedFormikTextField {...props} />;
});
